import React, { useEffect, useRef } from 'react';
import { isUnderWbsPath } from '../../utils/scheduleWbsOutline';

function TriCheckbox({ checked, indeterminate, onChange }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate && !checked;
  }, [checked, indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      style={{ accentColor: '#0284c7', width: 11, height: 11 }}
    />
  );
}

export function activityCodesUnderPath(outline, wbsPath) {
  return (outline || [])
    .filter((r) => r._kind === 'activity' && isUnderWbsPath(r._path, wbsPath))
    .map((r) => r.activity_code || r.activity_id)
    .filter(Boolean);
}

/**
 * Compact P6-style WBS table for the Activities filter popover.
 */
export default function ActivityWbsTree({
  rows,
  outline,
  selectedCodes,
  collapsedPaths,
  onToggleCode,
  onToggleCodes,
  onToggleCollapse,
  formatNumber
}) {
  const selected = new Set(selectedCodes || []);

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.65rem', tableLayout: 'fixed' }}>
      <colgroup>
        <col style={{ width: 16 }} />
        <col style={{ width: 18 }} />
        <col />
        <col style={{ width: '12%' }} />
        <col style={{ width: '12%' }} />
        <col style={{ width: '8%' }} />
        <col style={{ width: '8%' }} />
        <col style={{ width: '8%' }} />
      </colgroup>
      <thead>
        <tr style={{ color: '#64748b', textAlign: 'left' }}>
          <th style={{ fontWeight: 600, padding: '1px 2px' }} />
          <th style={{ fontWeight: 600, padding: '1px 2px' }} />
          <th style={{ fontWeight: 600, padding: '1px 2px' }}>WBS / Activity</th>
          <th style={{ fontWeight: 600, padding: '1px 2px' }}>Start</th>
          <th style={{ fontWeight: 600, padding: '1px 2px' }}>Finish</th>
          <th style={{ fontWeight: 600, padding: '1px 2px', textAlign: 'right' }}>Bud</th>
          <th style={{ fontWeight: 600, padding: '1px 2px', textAlign: 'right' }}>Act</th>
          <th style={{ fontWeight: 600, padding: '1px 2px', textAlign: 'right' }}>Rem</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={8} style={{ padding: '4px 2px', color: '#94a3b8' }}>No match.</td>
          </tr>
        ) : rows.map((row) => {
          const isWbs = row._kind === 'wbs';
          const indent = Math.max(0, Number(row._level) || 0) * 10;
          const codes = isWbs
            ? activityCodesUnderPath(outline, row._path)
            : [row.activity_code || row.activity_id].filter(Boolean);
          const onCount = codes.filter((c) => selected.has(c)).length;
          const allOn = codes.length > 0 && onCount === codes.length;
          const someOn = onCount > 0 && !allOn;
          const collapsed = isWbs && collapsedPaths?.has(row._path);
          const start = row.start || row.start_date || '—';
          const finish = row.finish || row.finish_date || '—';
          const name = isWbs
            ? row._label
            : [
              row.activity_code,
              row.activity_name && row.activity_name !== row.activity_code ? row.activity_name : ''
            ].filter(Boolean).join('  ');
          return (
            <tr
              key={isWbs ? `wbs:${row._path}` : row.activity_code}
              onClick={() => (isWbs ? onToggleCodes(codes) : onToggleCode(row.activity_code))}
              style={{
                cursor: 'pointer',
                background: allOn ? '#e0f2fe' : isWbs ? '#f8fafc' : 'transparent',
                color: allOn ? '#0f172a' : '#334155',
                fontWeight: isWbs ? 700 : 400
              }}
            >
              <td style={{ padding: '1px 0', textAlign: 'center' }}>
                {isWbs ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleCollapse(row._path);
                    }}
                    title={collapsed ? 'Expand' : 'Collapse'}
                    style={{
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      width: 14,
                      cursor: 'pointer',
                      color: '#64748b',
                      fontSize: '0.55rem',
                      lineHeight: 1
                    }}
                  >
                    {collapsed ? '▶' : '▼'}
                  </button>
                ) : null}
              </td>
              <td style={{ padding: '1px 2px' }}>
                <TriCheckbox
                  checked={allOn}
                  indeterminate={someOn}
                  onChange={() => (isWbs ? onToggleCodes(codes) : onToggleCode(row.activity_code))}
                />
              </td>
              <td
                style={{
                  padding: '1px 2px',
                  paddingLeft: 2 + indent,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={isWbs ? `${row._path} · ${codes.length} activities` : name}
              >
                {name || '—'}
                {isWbs ? (
                  <span style={{ fontWeight: 600, color: '#94a3b8', marginLeft: 6 }}>
                    {codes.length}
                  </span>
                ) : null}
              </td>
              <td style={{ padding: '1px 2px', whiteSpace: 'nowrap' }}>{start || '—'}</td>
              <td style={{ padding: '1px 2px', whiteSpace: 'nowrap' }}>{finish || '—'}</td>
              <td style={{ padding: '1px 2px', textAlign: 'right' }}>{formatNumber(row.budget_hours)}</td>
              <td style={{ padding: '1px 2px', textAlign: 'right' }}>{formatNumber(row.actual_hours)}</td>
              <td style={{ padding: '1px 2px', textAlign: 'right' }}>{formatNumber(row.remaining_hours ?? row.hours)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
